import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Mail, Phone, RotateCcw, AlertTriangle, Check, X, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending:  { background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  approved: { background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" },
  received: { background:"#e0e7ff", color:"#3730a3", border:"1px solid #a5b4fc" },
  credited: { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" },
  rejected: { background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" },
};

export default function AdminReturns() {
  const [data,           setData]           = useState({ items: [], pending: 0 });
  const [view,           setView]           = useState(null);
  const [decisionStatus, setDecisionStatus] = useState("approved");
  const [note,           setNote]           = useState("");

  const load = async () => {
    const { data } = await adminApi.get("/admin/returns");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    await adminApi.put(`/admin/returns/${view.id}`, { status: decisionStatus, note });
    toast.success(`Return ${decisionStatus}`);
    setView(null); setNote(""); load();
  };

  const inp = { background:"#ffffff", borderColor:"#dde3ef", color:"#0f172a" };

  return (
    <div className="space-y-5" data-testid="admin-returns-page">

      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Requests</div>
        <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Returns / RMA</h1>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          {data.pending} pending · Pinnacle term: <b>Return Merchandise Authorization</b>
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label:"Total",    value: data.items.length,                                         accent:"#1e5bb8" },
          { label:"Pending",  value: data.items.filter(r=>r.status==="pending").length,         accent:"#d97706" },
          { label:"Approved", value: data.items.filter(r=>r.status==="approved").length,        accent:"#7c3aed" },
          { label:"Credited", value: data.items.filter(r=>r.status==="credited").length,        accent:"#16a34a" },
        ].map(({ label, value, accent }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm"
            style={{ border:"1px solid #dde3ef", borderTop:`3px solid ${accent}` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{label}</div>
            <div className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["RMA #","Order","Customer","Items","Source","Status","Action"].map((h,i)=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em", textAlign:i>=6?"right":"left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, idx) => (
              <tr key={r.id} className="hover:bg-[#f5f7fb] transition-colors"
                style={{ borderBottom: idx < data.items.length-1 ? "1px solid #f0f4f8" : "none" }}
                data-testid={`return-row-${r.rma_number}`}>
                <td className="px-4 py-3">
                  <div className="font-heading font-bold text-[14px]" style={{ color:"#0f172a" }}>{r.rma_number}</div>
                </td>
                <td className="px-4 py-3 text-[13px] font-medium" style={{ color:"#1e5bb8" }}>{r.order_number}</td>
                <td className="px-4 py-3">
                  <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>{r.customer_name}</div>
                  <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color:"#64748b" }}>
                    <Mail className="h-3 w-3"/> {r.customer_email}
                  </div>
                  {r.contact_phone && (
                    <div className="flex items-center gap-1 text-[11px]" style={{ color:"#64748b" }}>
                      <Phone className="h-3 w-3"/> {r.contact_phone}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 max-w-[160px]">
                  <div className="text-[11px] font-mono truncate" style={{ color:"#64748b" }}>
                    {r.items.map(i=>`${i.qty}× ${i.sku}`).join(", ")}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="badge badge-gray">{r.source || "website"}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                    style={STATUS_STYLE[r.status] || {}}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setView(r); setDecisionStatus(r.status==="pending"?"approved":r.status); setNote(""); }}
                    className="h-8 px-3 rounded-lg text-[11px] font-semibold transition-colors"
                    style={{ background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" }}
                    data-testid={`return-view-${r.rma_number}`}>
                    Review
                  </button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="p-14 text-center">
                <RotateCcw className="h-10 w-10 mx-auto mb-3" style={{ color:"#c5cfe3" }}/>
                <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                  No return requests
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Review modal */}
      {view && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background:"rgba(0,0,0,0.5)" }} onClick={() => setView(null)}
          data-testid="return-detail-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            style={{ border:"1px solid #dde3ef" }} onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div className="p-5 flex items-center justify-between" style={{ borderBottom:"1px solid #eef2f8" }}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>{view.rma_number}</div>
                <div className="font-heading text-xl font-bold" style={{ color:"#0f172a" }}>Order {view.order_number}</div>
              </div>
              <button onClick={() => setView(null)} style={{ color:"#94a3b8" }}><X className="h-5 w-5"/></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Customer */}
              <div className="text-[13px]" style={{ color:"#475569" }}>
                <b style={{ color:"#0f172a" }}>{view.customer_name}</b>
                {" · "}{view.customer_email}
                {view.contact_phone && ` · ${view.contact_phone}`}
              </div>

              {/* Items */}
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Items requested</Label>
                <div className="mt-2 rounded-xl overflow-hidden" style={{ border:"1px solid #dde3ef" }}>
                  {view.items.map((i) => (
                    <div key={i.sku} className="p-3" style={{ borderBottom:"1px solid #f0f4f8" }}>
                      <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>{i.name}</div>
                      <div className="text-[10px] font-mono mt-0.5" style={{ color:"#94a3b8" }}>
                        {i.sku} · {formatAud(i.price)} · qty {i.qty}
                      </div>
                      {i.reason && <div className="text-[11px] mt-1" style={{ color:"#64748b" }}>Reason: {i.reason}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer reason */}
              {view.reason && (
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Customer reason</Label>
                  <div className="mt-1 text-[13px]" style={{ color:"#475569" }}>{view.reason}</div>
                </div>
              )}

              {/* Photos */}
              {view.images?.length > 0 && (
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide flex items-center gap-1" style={{ color:"#64748b" }}>
                    <ImgIcon className="h-3 w-3"/> Photos
                  </Label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {view.images.map((src, i) => (
                      <a key={i} href={src} target="_blank" rel="noreferrer"
                        className="aspect-square rounded-xl overflow-hidden" style={{ border:"1px solid #dde3ef" }}>
                        <img src={src} alt="" className="w-full h-full object-cover"/>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision */}
              {view.status === "pending" ? (
                <>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Decision</Label>
                    <Select value={decisionStatus} onValueChange={setDecisionStatus}>
                      <SelectTrigger className="mt-1 rounded-xl" style={inp} data-testid="return-decision-select">
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approve — send return label</SelectItem>
                        <SelectItem value="received">Received — awaiting inspection</SelectItem>
                        <SelectItem value="rejected">Reject</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Note to customer</Label>
                    <Input value={note} onChange={e=>setNote(e.target.value)}
                      className="mt-1 rounded-xl" style={inp} data-testid="return-note-input"/>
                  </div>
                  <button onClick={submit}
                    className="w-full h-11 rounded-xl text-white font-bold uppercase tracking-wide text-[13px] flex items-center justify-center gap-2"
                    style={{ background:"#e31e24" }} data-testid="return-submit-btn">
                    <Check className="h-4 w-4"/> Submit decision
                  </button>
                  <div className="flex items-start gap-2 p-3 rounded-xl text-[11px]"
                    style={{ background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e" }}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5"/>
                    Once items are <b>received</b>, go to <b>Credits</b> to issue the credit with restock toggle.
                  </div>
                </>
              ) : (
                <div className="p-3 rounded-xl text-[12px] font-mono uppercase tracking-widest"
                  style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}>
                  Already {view.status}{view.decided_by ? ` · by ${view.decided_by}` : ""}
                  {view.decision_note && (
                    <div className="mt-1 normal-case tracking-normal text-[13px]" style={{ color:"#475569" }}>
                      {view.decision_note}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}